import { useCallback, useState } from 'react';
import { ServerError } from '@apollo/client';
import { isNil } from 'ramda';

import useToast from 'components/ToastProvider/useToast';

import getErrorMessageIfPossible from './getErrorMessageIfPossible';
import locales from './locales';
import { UseMutationErrorHandledWrapper } from './types';

const isOutOfDateAuthToken = (networkError: ServerError) =>
  networkError.statusCode === 401;

const useMutationErrorHandledWrapper: UseMutationErrorHandledWrapper = (
  useMutationResult,
  handlerOptions = {},
) => {
  const [originalMutator, mutationResults] = useMutationResult;
  const [hasHandledError, setHasHandledError] = useState<boolean | undefined>(
    undefined,
  );
  const {
    handleUnrecognizedErrors = true,
    unrecognizedErrorMessage = locales.unrecognizedErrorMessage,
  } = handlerOptions;
  const { showToast } = useToast();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markErrorAsHandled = (error: any) => {
    setHasHandledError(true);
    // When this helper function is used, we know the error must be an ApolloError,
    // so it is safe to treat it as an object and add the isHandledError property
    // to it
    error.isHandledError = true;
  };

  const errorHandledMutator = useCallback(
    // TS error because of React 18 typings update
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...args: any[]) => {
      setHasHandledError(false);
      return originalMutator(...args).catch((error) => {
        // Casting to a ServerError below to match the same processing done
        // by the graphql middleware in ClientProvider to detect and emit
        // ON_UNAUTHORIZED events.
        if (
          !isNil(error.networkError) &&
          isOutOfDateAuthToken(error.networkError as ServerError)
        ) {
          // This is a normal part of operation and therefore should not
          // be handled with an error message.
          markErrorAsHandled(error);
        } else {
          const toastErrorMessage = getErrorMessageIfPossible({
            error,
            handleUnrecognizedErrors,
            unrecognizedErrorMessage,
          });
          if (toastErrorMessage !== null) {
            showToast({
              message: toastErrorMessage,
              type: 'error',
            });
            markErrorAsHandled(error);
          }
        }
        throw error;
      });
    },
    [
      originalMutator,
      handleUnrecognizedErrors,
      unrecognizedErrorMessage,
      showToast,
    ],
  );

  return [
    errorHandledMutator,
    {
      ...mutationResults,
      hasHandledError,
    },
  ];
};

export default useMutationErrorHandledWrapper;
